import { Container, Graphics, Matrix } from "pixi.js";
import type { Mesh, Texture } from "pixi.js";
import {
  BALL,
  COLORS,
  DEFAULT_PITCH_SKIN,
  FIELD,
  goalMouth,
  pitchSkin,
  type PitchSkinId,
} from "@golazo/shared";
import { drawPitchSurface } from "./pitchPatterns.js";
import { drawFutsalLines, isFutsalSkin } from "./futsalLines.js";
import { createGoalNets, type GoalNets } from "./goalNet.js";
import { getTextures } from "./textures.js";

/**
 * Dibuja el terreno de juego una sola vez. El contenedor devuelto usa
 * coordenadas de mundo con (0,0) en la esquina superior izquierda del campo,
 * asi que jugadores y campo comparten sistema de referencia.
 */
export interface FieldLayer {
  container: Container;
  /**
   * Las redes. Se devuelven porque hay que darles el impacto del gol y un
   * `update` por frame, y viven DENTRO de la porteria (por debajo de los
   * palos): sacarlas a una capa aparte las pondria por delante del marco.
   */
  nets: GoalNets;
}

export function createField(skinId: PitchSkinId = DEFAULT_PITCH_SKIN): FieldLayer {
  const skin = pitchSkin(skinId);
  const field = new Container();
  field.label = "field";

  const tex = getTextures();
  const goalNets = createGoalNets(tex?.net ?? null);

  field.addChild(createGrass(skin));
  // Entre la hierba y las lineas: el grano tiene que oscurecer el cesped, no
  // las lineas de cal ni las porterias.
  const turf = createTurf();
  if (turf) field.addChild(turf);
  field.addChild(createGoal("left", skin, goalNets.meshFor("left")));
  field.addChild(createGoal("right", skin, goalNets.meshFor("right")));
  field.addChild(createLines(skin));

  return { container: field, nets: goalNets.nets };
}

/**
 * Cuanto mundo cubre un tile de cesped. Con 240 el tile de 256 px cae casi a
 * pixel por pixel en pantalla: mas grande se emborrona, mas pequeno hace muare.
 */
const TURF_TILE_WORLD = 240;

/**
 * Relleno con textura repetida usando `Graphics`, no `TilingSprite`.
 *
 * Hacen lo mismo, pero importar `TilingSprite` mete su propio renderer en el
 * paquete: medido, 44 kB en crudo y 14 kB comprimidos por una tesela. El
 * relleno con matriz de `Graphics` usa maquinaria que el juego ya carga y sale
 * gratis.
 */
function tiledFill(
  rect: { x: number; y: number; width: number; height: number },
  texture: Texture,
  tileWorld: number,
): Graphics {
  const scale = tileWorld / texture.width;
  const g = new Graphics();
  g.rect(rect.x, rect.y, rect.width, rect.height).fill({
    texture,
    matrix: new Matrix().scale(scale, scale),
  });
  return g;
}

function createTurf(): Graphics | null {
  const tex = getTextures();
  if (!tex) return null;

  const turf = tiledFill(
    { x: 0, y: 0, width: FIELD.WIDTH, height: FIELD.HEIGHT },
    tex.turf,
    TURF_TILE_WORLD,
  );
  turf.label = "turf";
  // Multiplicar y no superponer: asi la textura solo puede OSCURECER, nunca
  // aclara. El verde de cada skin se mantiene exactamente donde estaba y lo
  // unico que aparece es el relieve.
  turf.blendMode = "multiply";
  return turf;
}

function createGrass(skin: ReturnType<typeof pitchSkin>): Graphics {
  const g = new Graphics();
  drawPitchSurface(g, skin);
  return g;
}

function createLines(skin: ReturnType<typeof pitchSkin>): Graphics {
  const g = new Graphics();
  if (isFutsalSkin(skin)) {
    drawFutsalLines(g, skin);
    return g;
  }

  const { WIDTH, HEIGHT, LINE_WIDTH, BOX_WIDTH, BOX_HEIGHT, CENTER_CIRCLE_RADIUS } = FIELD;
  const lineColor = skin.line || COLORS.LINE;
  const stroke = { width: LINE_WIDTH, color: lineColor, alpha: 0.88 } as const;

  g.rect(0, 0, WIDTH, HEIGHT).stroke(stroke);
  g.moveTo(WIDTH / 2, 0).lineTo(WIDTH / 2, HEIGHT).stroke(stroke);
  g.circle(WIDTH / 2, HEIGHT / 2, CENTER_CIRCLE_RADIUS).stroke(stroke);
  g.circle(WIDTH / 2, HEIGHT / 2, LINE_WIDTH * 1.8).fill(lineColor);

  const boxTop = (HEIGHT - BOX_HEIGHT) / 2;
  const smallWidth = BOX_WIDTH * 0.42;
  const smallHeight = BOX_HEIGHT * 0.48;
  const smallTop = (HEIGHT - smallHeight) / 2;

  g.rect(0, boxTop, BOX_WIDTH, BOX_HEIGHT).stroke(stroke);
  g.rect(WIDTH - BOX_WIDTH, boxTop, BOX_WIDTH, BOX_HEIGHT).stroke(stroke);
  g.rect(0, smallTop, smallWidth, smallHeight).stroke(stroke);
  g.rect(WIDTH - smallWidth, smallTop, smallWidth, smallHeight).stroke(stroke);

  return g;
}

function createGoal(
  side: "left" | "right",
  skin: ReturnType<typeof pitchSkin>,
  net: Mesh | null,
): Container {
  const group = new Container();
  group.label = `goal-${side}`;

  const { WIDTH, GOAL_DEPTH, LINE_WIDTH } = FIELD;
  const mouth = goalMouth();
  const GOAL_HEIGHT = mouth.half * 2;
  const top = mouth.top;
  const x = side === "left" ? -GOAL_DEPTH : WIDTH;
  const postX = side === "left" ? 0 : WIDTH;
  const postR = BALL.POST_RADIUS;
  const frameColor = skin.line || COLORS.GOAL_FRAME;

  // Tres capas, y el orden importa: hueco, red dentro, postes delante. La red
  // tiene que quedar POR DEBAJO de los palos porque en cenital el poste es un
  // objeto solido contra el que rebota el balon; con la red encima parece
  // colgada por delante de la porteria.
  const frame = new Graphics();
  frame
    .rect(x, top, GOAL_DEPTH, GOAL_HEIGHT)
    // El fondo de la porteria, mas solido: es el telon contra el que se lee la
    // malla. Con 0,55 se transparentaba el estadio y la red se perdia encima.
    .fill({ color: COLORS.GOAL_NET, alpha: 0.92 })
    .stroke({ width: LINE_WIDTH, color: frameColor, alpha: 0.95 });
  group.addChild(frame);

  // La red es una malla deformable (`goalNet.ts`). Sin textura no hay malla y
  // la porteria se queda como estaba: hueco oscuro y palos.
  if (net) group.addChild(net);

  const posts = new Graphics();

  // El larguero: en cenital es la linea que une los dos palos sobre la propia
  // linea de gol. Va fino y translucido a proposito — tiene que leerse como
  // estructura POR ENCIMA del cesped, no como una barrera que cierra la boca.
  // El balon se dibuja en una capa superior, asi que pasa visiblemente por
  // delante cuando va alto.
  posts
    .moveTo(postX, mouth.top)
    .lineTo(postX, mouth.bottom)
    .stroke({ width: postR * 0.7, color: frameColor, alpha: 0.34 });

  const postFill = { color: frameColor, alpha: 0.98 };
  const postStroke = { width: LINE_WIDTH * 0.9, color: 0xd8e8dc, alpha: 0.95 };
  posts.circle(postX, mouth.top, postR).fill(postFill).stroke(postStroke);
  posts.circle(postX, mouth.bottom, postR).fill(postFill).stroke(postStroke);
  group.addChild(posts);

  return group;
}
